import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS } from '../designSystem';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useBackNavigation, createBackButton } from '../hooks/useBackNavigation';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// State constants
const ENTITY_TYPES = ['LLC', 'Corp', 'S-Corp', 'Sole Prop', 'Partnership', 'Other'];

const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

export default function MerchantEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const { setHeaderConfig, resetHeaderConfig } = usePageHeader();
  const handleBack = useBackNavigation('merchant-edit', `/merchants/${id}`);

  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [existingAddresses, setExistingAddresses] = useState([]);
  const [existingOwners, setExistingOwners] = useState([]);
  const [existingBankAccounts, setExistingBankAccounts] = useState([]);
  const [existingEntities, setExistingEntities] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingOwner, setEditingOwner] = useState(null);
  const [editingBank, setEditingBank] = useState(null);
  const [editingEntity, setEditingEntity] = useState(null);
  const [addressForm, setAddressForm] = useState({
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
    is_primary: false,
  });
  const [addressError, setAddressError] = useState(null);
  const [addressSuccess, setAddressSuccess] = useState(null);
  const [ownerForm, setOwnerForm] = useState({
    full_name: '',
    title: '',
    ownership_percentage: '',
    email: '',
    phone: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
    ssn: '',
    driver_license_number: '',
    driver_license_state: '',
  });
  const [ownerError, setOwnerError] = useState(null);
  const [ownerSuccess, setOwnerSuccess] = useState(null);
  const [bankForm, setBankForm] = useState({
    account_name: '',
    bank_name: '',
    account_number: '',
    routing_number: '',
    is_primary: false,
    is_active: true,
  });
  const [bankError, setBankError] = useState(null);
  const [bankSuccess, setBankSuccess] = useState(null);
  const [entityForm, setEntityForm] = useState({
    legal_name: '',
    dba_name: '',
    ein: '',
    business_address_line_1: '',
    business_address_line_2: '',
    business_city: '',
    business_state: '',
    business_zip: '',
    contact_address: '',
  });
  const [entityError, setEntityError] = useState(null);
  const [entitySuccess, setEntitySuccess] = useState(null);

  useEffect(() => {
    const fetchMerchant = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/merchants/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setMerchant(res.data);
        setFormData(res.data);

        // Fetch existing sub-records
        const results = await Promise.allSettled([
          axios.get(`${API_URL}/merchants/${id}/addresses`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
          axios.get(`${API_URL}/merchants/${id}/owners`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
          axios.get(`${API_URL}/merchants/${id}/bank-accounts`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
          axios.get(`${API_URL}/merchants/${id}/additional-entities`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
        ]);

        if (results[0].status === 'fulfilled') setExistingAddresses(results[0].value.data);
        if (results[1].status === 'fulfilled') setExistingOwners(results[1].value.data);
        if (results[2].status === 'fulfilled') setExistingBankAccounts(results[2].value.data);
        if (results[3].status === 'fulfilled') setExistingEntities(results[3].value.data);
      } catch (err) {
        setError('Failed to load merchant');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchMerchant();
  }, [id, token]);

  // Set up unified header
  useEffect(() => {
    if (!merchant) return;

    setHeaderConfig({
      backButton: createBackButton(handleBack),
      title: 'Edit Merchant',
      subtitle: merchant.legal_name,
      actions: [
        <button key="save" onClick={handleSubmit} style={{
          padding: `${SPACING.SM} ${SPACING.LG}`,
          backgroundColor: COLORS.PRIMARY_DARK,
          color: COLORS.TEXT_WHITE,
          border: 'none',
          borderRadius: RADIUS.DEFAULT,
          fontSize: TYPOGRAPHY.BODY.fontSize,
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}>
          Save Changes
        </button>,
      ],
    });

    // Cleanup: reset header when unmounting
    return () => resetHeaderConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant, id, handleBack, setHeaderConfig, resetHeaderConfig]);

  const buildAddressString = (owner) => {
    const parts = [
      owner.address_line_1,
      owner.address_line_2,
      owner.city && owner.state && owner.zip
        ? `${owner.city}, ${owner.state} ${owner.zip}`
        : '',
    ].filter(part => part);
    return parts.join('\n');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API_URL}/merchants/${id}`, formData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      navigate(`/merchants/${id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save merchant');
    }
  };

  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddressForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setAddressError(null);
    setAddressSuccess(null);

    try {
      await axios.post(`${API_URL}/merchants/${id}/addresses`, addressForm, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setAddressSuccess('Address added successfully!');
      setAddressForm({
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        zip: '',
        is_primary: false,
      });
      // Clear success message after 3 seconds
      setTimeout(() => setAddressSuccess(null), 3000);
    } catch (err) {
      setAddressError(err.response?.data?.detail || 'Failed to add address');
    }
  };

  const handleOwnerChange = (e) => {
    const { name, value } = e.target;
    setOwnerForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOwnerSubmit = async (e) => {
    e.preventDefault();
    setOwnerError(null);
    setOwnerSuccess(null);

    try {
      const payload = {
        full_name: ownerForm.full_name,
        title: ownerForm.title || null,
        ownership_percentage: ownerForm.ownership_percentage ? parseInt(ownerForm.ownership_percentage) : null,
        email: ownerForm.email || null,
        phone: ownerForm.phone || null,
        address_line_1: ownerForm.address_line_1|| null,
        address_line_2: ownerForm.address_line_2 || null,
        city: ownerForm.city || null,
        state: ownerForm.state || null,
        zip: ownerForm.zip || null,
        ssn: ownerForm.ssn || null,
        driver_license_number: ownerForm.driver_license_number || null,
        driver_license_state: ownerForm.driver_license_state || null,
      };

      await axios.post(`${API_URL}/merchants/${id}/owners`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setOwnerSuccess('Owner added successfully!');
      setOwnerForm({
        full_name: '',
        title: '',
        ownership_percentage: '',
        email: '',
        phone: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        zip: '',
        ssn: '',
        driver_license_number: '',
        driver_license_state: '',
      });
      // Clear success message after 3 seconds
      setTimeout(() => setOwnerSuccess(null), 3000);
    } catch (err) {
      setOwnerError(err.response?.data?.detail || 'Failed to add owner');
    }
  };

  const handleBankChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBankForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    setBankError(null);
    setBankSuccess(null);

    try {
      const payload = {
        account_name: bankForm.account_name,
        bank_name: bankForm.bank_name,
        account_number: bankForm.account_number,
        routing_number: bankForm.routing_number || null,
        is_primary: bankForm.is_primary,
        is_active: bankForm.is_active,
      };

      await axios.post(`${API_URL}/merchants/${id}/bank-accounts`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setBankSuccess('Bank account added successfully!');
      setBankForm({
        account_name: '',
        bank_name: '',
        account_number: '',
        routing_number: '',
        is_primary: false,
        is_active: true,
      });
      // Clear success message after 3 seconds
      setTimeout(() => setBankSuccess(null), 3000);
    } catch (err) {
      setBankError(err.response?.data?.detail || 'Failed to add bank account');
    }
  };

  const handleEntityChange = (e) => {
    const { name, value } = e.target;
    setEntityForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEntitySubmit = async (e) => {
    e.preventDefault();
    setEntityError(null);
    setEntitySuccess(null);

    try {
      // Combine structured business address into single field
      const businessAddressParts = [
        entityForm.business_address_line_1,
        entityForm.business_address_line_2,
        entityForm.business_city && entityForm.business_state && entityForm.business_zip
          ? `${entityForm.business_city}, ${entityForm.business_state} ${entityForm.business_zip}`
          : '',
      ].filter(part => part).join('\n');

      const payload = {
        legal_name: entityForm.legal_name,
        dba_name: entityForm.dba_name || null,
        ein: entityForm.ein || null,
        business_address: businessAddressParts || null,
        contact_address: entityForm.contact_address || null,
      };

      await axios.post(`${API_URL}/merchants/${id}/additional-entities`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setEntitySuccess('Additional entity added successfully!');
      // Auto-populate contact_address from primary owner for next entity
      const primaryOwnerAddress = existingOwners.length > 0 ? buildAddressString(existingOwners[0]) : '';
      setEntityForm({
        legal_name: '',
        dba_name: '',
        ein: '',
        business_address_line_1: '',
        business_address_line_2: '',
        business_city: '',
        business_state: '',
        business_zip: '',
        contact_address: primaryOwnerAddress,
      });
      // Refresh entities list (separate error handling to not overwrite success message)
      try {
        const res = await axios.get(`${API_URL}/merchants/${id}/additional-entities`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        setExistingEntities(res.data);
      } catch (refreshErr) {
        console.error('Failed to refresh entities list:', refreshErr);
      }
      // Clear success message after 3 seconds
      setTimeout(() => setEntitySuccess(null), 3000);
    } catch (err) {
      setEntityError(err.response?.data?.detail || 'Failed to add entity');
    }
  };

  // Update/Delete handlers
  const handleUpdateAddress = async (addressId, updatedData) => {
    try {
      await axios.patch(`${API_URL}/merchants/${id}/addresses/${addressId}`, updatedData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const res = await axios.get(`${API_URL}/merchants/${id}/addresses`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setExistingAddresses(res.data);
      setEditingAddress(null);
    } catch (err) {
      console.error('Failed to update address:', err);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    try {
      await axios.delete(`${API_URL}/merchants/${id}/addresses/${addressId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const res = await axios.get(`${API_URL}/merchants/${id}/addresses`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setExistingAddresses(res.data);
    } catch (err) {
      console.error('Failed to delete address:', err);
    }
  };

  const handleUpdateOwner = async (ownerId, updatedData) => {
    try {
      await axios.patch(`${API_URL}/merchants/${id}/owners/${ownerId}`, updatedData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const res = await axios.get(`${API_URL}/merchants/${id}/owners`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setExistingOwners(res.data);
      setEditingOwner(null);
    } catch (err) {
      console.error('Failed to update owner:', err);
    }
  };

  const handleDeleteOwner = async (ownerId) => {
    if (!window.confirm('Are you sure you want to delete this owner?')) return;
    try {
      await axios.delete(`${API_URL}/merchants/${id}/owners/${ownerId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const res = await axios.get(`${API_URL}/merchants/${id}/owners`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setExistingOwners(res.data);
    } catch (err) {
      console.error('Failed to delete owner:', err);
    }
  };

  const handleUpdateBank = async (bankId, updatedData) => {
    try {
      await axios.patch(`${API_URL}/merchants/${id}/bank-accounts/${bankId}`, updatedData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const res = await axios.get(`${API_URL}/merchants/${id}/bank-accounts`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setExistingBankAccounts(res.data);
      setEditingBank(null);
    } catch (err) {
      console.error('Failed to update bank account:', err);
    }
  };

  const handleDeleteBank = async (bankId) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    try {
      await axios.delete(`${API_URL}/merchants/${id}/bank-accounts/${bankId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const res = await axios.get(`${API_URL}/merchants/${id}/bank-accounts`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setExistingBankAccounts(res.data);
    } catch (err) {
      console.error('Failed to delete bank account:', err);
    }
  };

  const handleUpdateEntity = async (entityId, updatedData) => {
    try {
      await axios.patch(`${API_URL}/merchants/${id}/additional-entities/${entityId}`, updatedData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const res = await axios.get(`${API_URL}/merchants/${id}/additional-entities`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setExistingEntities(res.data);
      setEditingEntity(null);
    } catch (err) {
      console.error('Failed to update entity:', err);
    }
  };

  const handleDeleteEntity = async (entityId) => {
    if (!window.confirm('Are you sure you want to delete this entity?')) return;
    try {
      await axios.delete(`${API_URL}/merchants/${id}/additional-entities/${entityId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const res = await axios.get(`${API_URL}/merchants/${id}/additional-entities`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setExistingEntities(res.data);
    } catch (err) {
      console.error('Failed to delete entity:', err);
    }
  };

  // Handlers to start editing existing items
  const startEditAddress = (addr) => {
    setAddressForm(addr);
    setEditingAddress(addr.id);
  };

  const cancelEditAddress = () => {
    setEditingAddress(null);
    setAddressForm({
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zip: '',
      is_primary: false,
    });
  };

  const saveEditAddress = async (addressId) => {
    const updatedData = {
      address_line_1: addressForm.address_line_1,
      address_line_2: addressForm.address_line_2 || null,
      city: addressForm.city,
      state: addressForm.state,
      zip: addressForm.zip,
      is_primary: addressForm.is_primary,
    };
    await handleUpdateAddress(addressId, updatedData);
    cancelEditAddress();
  };

  const startEditOwner = (owner) => {
    setOwnerForm(owner);
    setEditingOwner(owner.id);
  };

  const cancelEditOwner = () => {
    setEditingOwner(null);
    setOwnerForm({
      full_name: '',
      title: '',
      ownership_percentage: '',
      email: '',
      phone: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zip: '',
      ssn: '',
      driver_license_number: '',
      driver_license_state: '',
    });
  };

  const saveEditOwner = async (ownerId) => {
    const updatedData = {
      full_name: ownerForm.full_name,
      title: ownerForm.title || null,
      ownership_percentage: ownerForm.ownership_percentage ? parseInt(ownerForm.ownership_percentage) : null,
      email: ownerForm.email || null,
      phone: ownerForm.phone || null,
      address_line_1: ownerForm.address_line_1 || null,
      address_line_2: ownerForm.address_line_2 || null,
      city: ownerForm.city || null,
      state: ownerForm.state || null,
      zip: ownerForm.zip || null,
      ssn: ownerForm.ssn || null,
      driver_license_number: ownerForm.driver_license_number || null,
      driver_license_state: ownerForm.driver_license_state || null,
    };
    await handleUpdateOwner(ownerId, updatedData);
    cancelEditOwner();
  };

  const startEditBank = (bank) => {
    setBankForm(bank);
    setEditingBank(bank.id);
  };

  const cancelEditBank = () => {
    setEditingBank(null);
    setBankForm({
      account_name: '',
      bank_name: '',
      account_number: '',
      routing_number: '',
      is_primary: false,
      is_active: true,
    });
  };

  const saveEditBank = async (bankId) => {
    const updatedData = {
      account_name: bankForm.account_name,
      bank_name: bankForm.bank_name,
      account_number: bankForm.account_number,
      routing_number: bankForm.routing_number || null,
      is_primary: bankForm.is_primary,
      is_active: bankForm.is_active,
    };
    await handleUpdateBank(bankId, updatedData);
    cancelEditBank();
  };

  const parseBusinessAddress = (addressString) => {
    if (!addressString) {
      return { line1: '', line2: '', city: '', state: '', zip: '' };
    }
    const lines = addressString.split('\n');
    let line1 = '', line2 = '', city = '', state = '', zip = '';

    if (lines.length >= 1) line1 = lines[0];
    if (lines.length >= 2) line2 = lines[1];
    if (lines.length >= 3) {
      // Last line is expected to be "city, state zip"
      const lastLine = lines[lines.length - 1];
      const match = lastLine.match(/^(.+),\s+([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/);
      if (match) {
        city = match[1];
        state = match[2];
        zip = match[3];
      }
    }
    return { line1, line2, city, state, zip };
  };

  const startEditEntity = (entity) => {
    const { line1, line2, city, state, zip } = parseBusinessAddress(entity.business_address);
    setEntityForm({
      legal_name: entity.legal_name,
      dba_name: entity.dba_name || '',
      ein: entity.ein || '',
      business_address_line_1: line1,
      business_address_line_2: line2,
      business_city: city,
      business_state: state,
      business_zip: zip,
      contact_address: entity.contact_address || '',
    });
    setEditingEntity(entity.id);
  };

  const cancelEditEntity = () => {
    setEditingEntity(null);
    setEntityForm({
      legal_name: '',
      dba_name: '',
      ein: '',
      business_address_line_1: '',
      business_address_line_2: '',
      business_city: '',
      business_state: '',
      business_zip: '',
      contact_address: '',
    });
  };

  const saveEditEntity = async (entityId) => {
    const businessAddressParts = [
      entityForm.business_address_line_1,
      entityForm.business_address_line_2,
      entityForm.business_city && entityForm.business_state && entityForm.business_zip
        ? `${entityForm.business_city}, ${entityForm.business_state} ${entityForm.business_zip}`
        : '',
    ].filter(part => part).join('\n');

    const updatedData = {
      legal_name: entityForm.legal_name,
      dba_name: entityForm.dba_name || null,
      ein: entityForm.ein || null,
      business_address: businessAddressParts || null,
      contact_address: entityForm.contact_address || null,
    };
    await handleUpdateEntity(entityId, updatedData);
    cancelEditEntity();
  };

  if (loading) return <div style={styles.container}><p>Loading...</p></div>;
  if (!merchant) return <div style={styles.container}><p>Merchant not found</p></div>;

  return (
    <div style={styles.container}>
      {error && <div style={styles.error}>{error}</div>}
      {/* Main Grid Layout */}
      <div style={styles.gridLayout}>
        {/* LEFT COLUMN */}
        <div>
          {/* Merchant Information Card */}
          <form id="merchantForm" onSubmit={handleSubmit} style={styles.card}>
            <h3 style={styles.sectionTitle}>Merchant Information</h3>
            <div style={styles.compactFormGroup}>
              <label style={styles.label}>Legal Name</label>
              <input
                type="text"
                name="legal_name"
                value={formData.legal_name || ''}
                onChange={handleChange}
                style={styles.input}
              />
            </div>
            <div style={styles.compactFormGroup}>
              <label style={styles.label}>DBA Name</label>
              <input
                type="text"
                name="dba_name"
                value={formData.dba_name || ''}
                onChange={handleChange}
                style={styles.input}
              />
            </div>
            <div style={styles.compactFormGroup}>
              <label style={styles.label}>EIN</label>
              <input
                type="text"
                name="ein"
                value={formData.ein || ''}
                onChange={handleChange}
                style={styles.input}
              />
            </div>
            <div style={styles.compactFormGroup}>
              <label style={styles.label}>Entity Type</label>
              <select
                name="entity_type"
                value={formData.entity_type || ''}
                onChange={handleChange}
                style={styles.input}
              >
                <option value="">— Select Entity Type —</option>
                {ENTITY_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            <div style={styles.compactFormGroup}>
              <label style={styles.label}>State of Formation</label>
              <select
                name="state_of_formation"
                value={formData.state_of_formation || ''}
                onChange={handleChange}
                style={styles.input}
              >
                <option value="">— Select State —</option>
                {US_STATES.map(state => <option key={state.code} value={state.code}>{state.name}</option>)}
              </select>
            </div>
          </form>

          {/* Addresses Card */}
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Addresses {existingAddresses.length > 0 && `(${existingAddresses.length})`}</h3>

            {/* Existing Addresses */}
            {existingAddresses.map(addr => (
              <div key={addr.id} style={{ marginBottom: SPACING.MD }}>
                {editingAddress === addr.id ? (
                  <div style={{...styles.existingItemContainer, borderLeft: `4px solid ${COLORS.PRIMARY}`}}>
                    <div style={{...TYPOGRAPHY.LABEL, marginBottom: SPACING.SM, color: COLORS.PRIMARY}}>Editing Address</div>
                    <div style={styles.subForm}>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Street Address *</label>
                        <input type="text" name="address_line_1" value={addressForm.address_line_1} onChange={handleAddressChange} style={styles.input} required />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Apt/Suite</label>
                        <input type="text" name="address_line_2" value={addressForm.address_line_2} onChange={handleAddressChange} style={styles.input} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING.SM }}>
                        <div style={styles.compactFormGroup}>
                          <label style={styles.label}>City *</label>
                          <input type="text" name="city" value={addressForm.city} onChange={handleAddressChange} style={styles.input} required />
                        </div>
                        <div style={styles.compactFormGroup}>
                          <label style={styles.label}>State *</label>
                          <select name="state" value={addressForm.state} onChange={handleAddressChange} style={styles.input} required>
                            <option value="">— Select —</option>
                            {US_STATES.map(state => <option key={state.code} value={state.code}>{state.name}</option>)}
                          </select>
                        </div>
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>ZIP *</label>
                        <input type="text" name="zip" value={addressForm.zip} onChange={handleAddressChange} style={styles.input} required />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={{ ...styles.label, display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                          <input type="checkbox" name="is_primary" checked={addressForm.is_primary} onChange={handleAddressChange} style={{ marginRight: SPACING.XS, cursor: 'pointer' }} />
                          <span>Primary</span>
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: SPACING.XS, marginTop: SPACING.SM }}>
                        <button onClick={() => saveEditAddress(addr.id)} style={styles.buttonSmall}>Save</button>
                        <button onClick={cancelEditAddress} style={{...styles.buttonSmall, backgroundColor: '#999'}}>Cancel</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={styles.existingItemContainer}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{...TYPOGRAPHY.BODY_SM, fontWeight: '600'}}>{addr.address_line_1} {addr.address_line_2 && <span>{addr.address_line_2}</span>}</div>
                        <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY}}>{addr.city}, {addr.state} {addr.zip}</div>
                        {addr.is_primary && <div style={{...TYPOGRAPHY.LABEL, color: COLORS.PRIMARY, fontSize: '10px'}}>Primary</div>}
                      </div>
                      <div style={{ display: 'flex', gap: SPACING.XS }}>
                        <button onClick={() => startEditAddress(addr)} style={styles.buttonTiny}>Edit</button>
                        <button onClick={() => handleDeleteAddress(addr.id)} style={{...styles.buttonTiny, backgroundColor: COLORS.ERROR}}>Delete</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {addressError && <div style={styles.error}>{addressError}</div>}
            {addressSuccess && <div style={{ ...styles.error, backgroundColor: '#d1fae5', color: '#065f46' }}>{addressSuccess}</div>}

            <div style={{paddingTop: SPACING.SM, borderTop: `1px solid ${COLORS.BORDER_LIGHT}`, marginTop: SPACING.SM}}>
              <form onSubmit={handleAddressSubmit} style={styles.subForm}>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Street Address *</label>
                <input type="text" name="address_line_1" value={addressForm.address_line_1} onChange={handleAddressChange} placeholder="Street address" style={styles.input} required />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Apt/Suite</label>
                <input type="text" name="address_line_2" value={addressForm.address_line_2} onChange={handleAddressChange} placeholder="Suite, apt, etc." style={styles.input} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING.SM }}>
                <div style={styles.compactFormGroup}>
                  <label style={styles.label}>City *</label>
                  <input type="text" name="city" value={addressForm.city} onChange={handleAddressChange} placeholder="City" style={styles.input} required />
                </div>
                <div style={styles.compactFormGroup}>
                  <label style={styles.label}>State *</label>
                  <select name="state" value={addressForm.state} onChange={handleAddressChange} style={styles.input} required>
                    <option value="">— Select —</option>
                    {US_STATES.map(state => <option key={state.code} value={state.code}>{state.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>ZIP *</label>
                <input type="text" name="zip" value={addressForm.zip} onChange={handleAddressChange} placeholder="10011" style={styles.input} required />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={{ ...styles.label, display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                  <input type="checkbox" name="is_primary" checked={addressForm.is_primary} onChange={handleAddressChange} style={{ marginRight: SPACING.XS, cursor: 'pointer' }} />
                  <span>Primary</span>
                </label>
              </div>
              <button type="submit" style={styles.buttonSmall}>+ Add Address</button>
              </form>
            </div>
          </div>

          {/* Bank Accounts Card */}
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Bank Accounts {existingBankAccounts.length > 0 && `(${existingBankAccounts.length})`}</h3>

            {/* Existing Bank Accounts */}
            {existingBankAccounts.map(bank => (
              <div key={bank.id} style={{ marginBottom: SPACING.MD }}>
                {editingBank === bank.id ? (
                  <div style={{...styles.existingItemContainer, borderLeft: `4px solid ${COLORS.PRIMARY}`}}>
                    <div style={{...TYPOGRAPHY.LABEL, marginBottom: SPACING.SM, color: COLORS.PRIMARY}}>Editing Bank Account</div>
                    <div style={styles.subForm}>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Account Name *</label>
                        <input type="text" name="account_name" value={bankForm.account_name} onChange={handleBankChange} style={styles.input} required />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Bank Name *</label>
                        <input type="text" name="bank_name" value={bankForm.bank_name} onChange={handleBankChange} style={styles.input} required />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Account Number *</label>
                        <input type="text" name="account_number" value={bankForm.account_number} onChange={handleBankChange} style={styles.input} required />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Routing Number</label>
                        <input type="text" name="routing_number" value={bankForm.routing_number} onChange={handleBankChange} style={styles.input} />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={{ ...styles.label, display: 'flex', alignItems: 'center', marginBottom: SPACING.XS }}>
                          <input type="checkbox" name="is_primary" checked={bankForm.is_primary} onChange={handleBankChange} style={{ marginRight: SPACING.XS, cursor: 'pointer' }} />
                          <span>Primary</span>
                        </label>
                        <label style={{ ...styles.label, display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                          <input type="checkbox" name="is_active" checked={bankForm.is_active} onChange={handleBankChange} style={{ marginRight: SPACING.XS, cursor: 'pointer' }} />
                          <span>Active</span>
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: SPACING.XS, marginTop: SPACING.SM }}>
                        <button onClick={() => saveEditBank(bank.id)} style={styles.buttonSmall}>Save</button>
                        <button onClick={cancelEditBank} style={{...styles.buttonSmall, backgroundColor: '#999'}}>Cancel</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={styles.existingItemContainer}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{...TYPOGRAPHY.BODY_SM, fontWeight: '600'}}>{bank.account_name} ({bank.bank_name})</div>
                        <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY, fontFamily: 'monospace'}}>••••{String(bank.account_number).slice(-4)}</div>
                        <div style={{ display: 'flex', gap: SPACING.XS, marginTop: SPACING.XS }}>
                          {bank.is_primary && <span style={{...TYPOGRAPHY.LABEL, color: COLORS.PRIMARY, fontSize: '10px'}}>Primary</span>}
                          {!bank.is_active && <span style={{...TYPOGRAPHY.LABEL, color: '#999', fontSize: '10px'}}>Inactive</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: SPACING.XS }}>
                        <button onClick={() => startEditBank(bank)} style={styles.buttonTiny}>Edit</button>
                        <button onClick={() => handleDeleteBank(bank.id)} style={{...styles.buttonTiny, backgroundColor: COLORS.ERROR}}>Delete</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {bankError && <div style={styles.error}>{bankError}</div>}
            {bankSuccess && <div style={{ ...styles.error, backgroundColor: '#d1fae5', color: '#065f46' }}>{bankSuccess}</div>}

            <div style={{paddingTop: SPACING.SM, borderTop: `1px solid ${COLORS.BORDER_LIGHT}`, marginTop: SPACING.SM}}>
              <form onSubmit={handleBankSubmit} style={styles.subForm}>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Account Name *</label>
                <input type="text" name="account_name" value={bankForm.account_name} onChange={handleBankChange} placeholder="Operating Account" style={styles.input} required />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Bank Name *</label>
                <input type="text" name="bank_name" value={bankForm.bank_name} onChange={handleBankChange} placeholder="Chase" style={styles.input} required />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Account Number *</label>
                <input type="text" name="account_number" value={bankForm.account_number} onChange={handleBankChange} placeholder="123456789" style={styles.input} required />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Routing Number</label>
                <input type="text" name="routing_number" value={bankForm.routing_number} onChange={handleBankChange} placeholder="021000021" style={styles.input} />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={{ ...styles.label, display: 'flex', alignItems: 'center', marginBottom: SPACING.XS }}>
                  <input type="checkbox" name="is_primary" checked={bankForm.is_primary} onChange={handleBankChange} style={{ marginRight: SPACING.XS, cursor: 'pointer' }} />
                  <span>Primary</span>
                </label>
                <label style={{ ...styles.label, display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                  <input type="checkbox" name="is_active" checked={bankForm.is_active} onChange={handleBankChange} style={{ marginRight: SPACING.XS, cursor: 'pointer' }} />
                  <span>Active</span>
                </label>
              </div>
              <button type="submit" style={styles.buttonSmall}>+ Add Account</button>
              </form>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div>
          {/* Owners Card */}
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Owners / Guarantors {existingOwners.length > 0 && `(${existingOwners.length})`}</h3>

            {/* Existing Owners */}
            {existingOwners.map(owner => (
              <div key={owner.id} style={{ marginBottom: SPACING.MD }}>
                {editingOwner === owner.id ? (
                  <div style={{...styles.existingItemContainer, borderLeft: `4px solid ${COLORS.PRIMARY}`}}>
                    <div style={{...TYPOGRAPHY.LABEL, marginBottom: SPACING.SM, color: COLORS.PRIMARY}}>Editing Owner</div>
                    <div style={styles.subForm}>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Full Name *</label>
                        <input type="text" name="full_name" value={ownerForm.full_name} onChange={handleOwnerChange} style={styles.input} required />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Title</label>
                        <input type="text" name="title" value={ownerForm.title} onChange={handleOwnerChange} style={styles.input} />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Ownership %</label>
                        <input type="number" name="ownership_percentage" value={ownerForm.ownership_percentage} onChange={handleOwnerChange} min="0" max="100" style={styles.input} />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Email</label>
                        <input type="email" name="email" value={ownerForm.email} onChange={handleOwnerChange} style={styles.input} />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Phone</label>
                        <input type="tel" name="phone" value={ownerForm.phone} onChange={handleOwnerChange} style={styles.input} />
                      </div>
                      <div style={{...styles.compactFormGroup, borderTop: `1px solid ${COLORS.BORDER_LIGHT}`, paddingTop: SPACING.SM, marginTop: SPACING.SM}}>
                        <label style={{...styles.label, fontSize: '11px', color: COLORS.TEXT_SECONDARY}}>Address Information</label>
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Street Address</label>
                        <input type="text" name="address_line_1" value={ownerForm.address_line_1} onChange={handleOwnerChange} style={styles.input} />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Apt/Suite</label>
                        <input type="text" name="address_line_2" value={ownerForm.address_line_2} onChange={handleOwnerChange} style={styles.input} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING.SM }}>
                        <div style={styles.compactFormGroup}>
                          <label style={styles.label}>City</label>
                          <input type="text" name="city" value={ownerForm.city} onChange={handleOwnerChange} style={styles.input} />
                        </div>
                        <div style={styles.compactFormGroup}>
                          <label style={styles.label}>State</label>
                          <select name="state" value={ownerForm.state} onChange={handleOwnerChange} style={styles.input}>
                            <option value="">— Select —</option>
                            {US_STATES.map(state => <option key={state.code} value={state.code}>{state.name}</option>)}
                          </select>
                        </div>
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>ZIP</label>
                        <input type="text" name="zip" value={ownerForm.zip} onChange={handleOwnerChange} style={styles.input} />
                      </div>
                      <div style={{...styles.compactFormGroup, borderTop: `1px solid ${COLORS.BORDER_LIGHT}`, paddingTop: SPACING.SM, marginTop: SPACING.SM}}>
                        <label style={{...styles.label, fontSize: '11px', color: COLORS.TEXT_SECONDARY}}>Identity Information</label>
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>SSN</label>
                        <input type="password" name="ssn" value={ownerForm.ssn} onChange={handleOwnerChange} style={styles.input} />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Driver License Number</label>
                        <input type="password" name="driver_license_number" value={ownerForm.driver_license_number} onChange={handleOwnerChange} style={styles.input} />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Driver License State</label>
                        <select name="driver_license_state" value={ownerForm.driver_license_state} onChange={handleOwnerChange} style={styles.input}>
                          <option value="">— Select —</option>
                          {US_STATES.map(state => <option key={state.code} value={state.code}>{state.name}</option>)}
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: SPACING.XS, marginTop: SPACING.SM }}>
                        <button onClick={() => saveEditOwner(owner.id)} style={styles.buttonSmall}>Save</button>
                        <button onClick={cancelEditOwner} style={{...styles.buttonSmall, backgroundColor: '#999'}}>Cancel</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={styles.existingItemContainer}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{...TYPOGRAPHY.BODY_SM, fontWeight: '600'}}>{owner.full_name} {owner.title && <span style={{color: COLORS.TEXT_SECONDARY}}>({owner.title})</span>}</div>
                        {owner.ownership_percentage !== null && <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY}}>{owner.ownership_percentage}% ownership</div>}
                        {owner.email && <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY, fontSize: '11px'}}>{owner.email}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: SPACING.XS }}>
                        <button onClick={() => startEditOwner(owner)} style={styles.buttonTiny}>Edit</button>
                        <button onClick={() => handleDeleteOwner(owner.id)} style={{...styles.buttonTiny, backgroundColor: COLORS.ERROR}}>Delete</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {ownerError && <div style={styles.error}>{ownerError}</div>}
            {ownerSuccess && <div style={{ ...styles.error, backgroundColor: '#d1fae5', color: '#065f46' }}>{ownerSuccess}</div>}

            <div style={{paddingTop: SPACING.SM, borderTop: `1px solid ${COLORS.BORDER_LIGHT}`, marginTop: SPACING.SM}}>
              <form onSubmit={handleOwnerSubmit} style={styles.subForm}>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Full Name *</label>
                <input type="text" name="full_name" value={ownerForm.full_name} onChange={handleOwnerChange} placeholder="John Smith" style={styles.input} required />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Title</label>
                <input type="text" name="title" value={ownerForm.title} onChange={handleOwnerChange} placeholder="Owner, CEO, etc." style={styles.input} />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Ownership %</label>
                <input type="number" name="ownership_percentage" value={ownerForm.ownership_percentage} onChange={handleOwnerChange} placeholder="50" min="0" max="100" style={styles.input} />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Email</label>
                <input type="email" name="email" value={ownerForm.email} onChange={handleOwnerChange} placeholder="john@example.com" style={styles.input} />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Phone</label>
                <input type="tel" name="phone" value={ownerForm.phone} onChange={handleOwnerChange} placeholder="555-123-4567" style={styles.input} />
              </div>
              <div style={{...styles.compactFormGroup, borderTop: `1px solid ${COLORS.BORDER_LIGHT}`, paddingTop: SPACING.SM, marginTop: SPACING.SM}}>
                <label style={{...styles.label, fontSize: '11px', color: COLORS.TEXT_SECONDARY}}>Address Information</label>
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Street Address</label>
                <input type="text" name="address_line_1" value={ownerForm.address_line_1} onChange={handleOwnerChange} placeholder="Street address" style={styles.input} />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Apt/Suite</label>
                <input type="text" name="address_line_2" value={ownerForm.address_line_2} onChange={handleOwnerChange} placeholder="Suite, apt, etc." style={styles.input} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING.SM }}>
                <div style={styles.compactFormGroup}>
                  <label style={styles.label}>City</label>
                  <input type="text" name="city" value={ownerForm.city} onChange={handleOwnerChange} placeholder="City" style={styles.input} />
                </div>
                <div style={styles.compactFormGroup}>
                  <label style={styles.label}>State</label>
                  <select name="state" value={ownerForm.state} onChange={handleOwnerChange} style={styles.input}>
                    <option value="">— Select —</option>
                    {US_STATES.map(state => <option key={state.code} value={state.code}>{state.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>ZIP</label>
                <input type="text" name="zip" value={ownerForm.zip} onChange={handleOwnerChange} placeholder="10011" style={styles.input} />
              </div>
              <div style={{...styles.compactFormGroup, borderTop: `1px solid ${COLORS.BORDER_LIGHT}`, paddingTop: SPACING.SM, marginTop: SPACING.SM}}>
                <label style={{...styles.label, fontSize: '11px', color: COLORS.TEXT_SECONDARY}}>Identity Information</label>
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>SSN</label>
                <input type="password" name="ssn" value={ownerForm.ssn} onChange={handleOwnerChange} placeholder="123-45-6789" style={styles.input} />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Driver License Number</label>
                <input type="password" name="driver_license_number" value={ownerForm.driver_license_number} onChange={handleOwnerChange} placeholder="Driver license number" style={styles.input} />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Driver License State</label>
                <select name="driver_license_state" value={ownerForm.driver_license_state} onChange={handleOwnerChange} style={styles.input}>
                  <option value="">— Select —</option>
                  {US_STATES.map(state => <option key={state.code} value={state.code}>{state.name}</option>)}
                </select>
              </div>
              <button type="submit" style={styles.buttonSmall}>+ Add Owner</button>
              </form>
            </div>
          </div>

          {/* Additional Entities Card */}
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Related Entities {existingEntities.length > 0 && `(${existingEntities.length})`}</h3>

            {/* Existing Entities */}
            {existingEntities.map(entity => (
              <div key={entity.id} style={{ marginBottom: SPACING.MD }}>
                {editingEntity === entity.id ? (
                  <div style={{...styles.existingItemContainer, borderLeft: `4px solid ${COLORS.PRIMARY}`}}>
                    <div style={{...TYPOGRAPHY.LABEL, marginBottom: SPACING.SM, color: COLORS.PRIMARY}}>Editing Entity</div>
                    <div style={styles.subForm}>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Legal Name *</label>
                        <input type="text" name="legal_name" value={entityForm.legal_name} onChange={handleEntityChange} style={styles.input} required />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>DBA Name</label>
                        <input type="text" name="dba_name" value={entityForm.dba_name} onChange={handleEntityChange} style={styles.input} />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>EIN</label>
                        <input type="text" name="ein" value={entityForm.ein} onChange={handleEntityChange} style={styles.input} />
                      </div>
                      {/* Business Address - Structured Fields */}
                      <div style={{...styles.compactFormGroup, borderTop: `1px solid ${COLORS.BORDER_LIGHT}`, paddingTop: SPACING.SM, marginTop: SPACING.SM}}>
                        <label style={{...styles.label, fontSize: '11px', color: COLORS.TEXT_SECONDARY}}>Business Address</label>
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Street Address</label>
                        <input type="text" name="business_address_line_1" value={entityForm.business_address_line_1} onChange={handleEntityChange} placeholder="Street address" style={styles.input} />
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>Apt/Suite</label>
                        <input type="text" name="business_address_line_2" value={entityForm.business_address_line_2} onChange={handleEntityChange} placeholder="Suite, apt, etc." style={styles.input} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING.SM }}>
                        <div style={styles.compactFormGroup}>
                          <label style={styles.label}>City</label>
                          <input type="text" name="business_city" value={entityForm.business_city} onChange={handleEntityChange} placeholder="City" style={styles.input} />
                        </div>
                        <div style={styles.compactFormGroup}>
                          <label style={styles.label}>State</label>
                          <select name="business_state" value={entityForm.business_state} onChange={handleEntityChange} style={styles.input}>
                            <option value="">— Select —</option>
                            {US_STATES.map(state => <option key={state.code} value={state.code}>{state.name}</option>)}
                          </select>
                        </div>
                      </div>
                      <div style={styles.compactFormGroup}>
                        <label style={styles.label}>ZIP</label>
                        <input type="text" name="business_zip" value={entityForm.business_zip} onChange={handleEntityChange} placeholder="ZIP code" style={styles.input} />
                      </div>
                      {/* Contact Address - Auto-populated from Primary Owner */}
                      <div style={{...styles.compactFormGroup, borderTop: `1px solid ${COLORS.BORDER_LIGHT}`, paddingTop: SPACING.SM, marginTop: SPACING.SM}}>
                        <label style={styles.label}>Contact Address</label>
                        <div style={{...TYPOGRAPHY.LABEL, fontSize: '11px', color: COLORS.TEXT_SECONDARY, marginBottom: SPACING.XS}}>Auto-filled from primary owner</div>
                        <textarea name="contact_address" value={entityForm.contact_address} onChange={handleEntityChange} placeholder="(auto-populated)" style={{ ...styles.input, minHeight: '60px', fontFamily: 'monospace', backgroundColor: '#f9fafb' }} />
                      </div>
                      <div style={{ display: 'flex', gap: SPACING.XS, marginTop: SPACING.SM }}>
                        <button onClick={() => saveEditEntity(entity.id)} style={styles.buttonSmall}>Save</button>
                        <button onClick={cancelEditEntity} style={{...styles.buttonSmall, backgroundColor: '#999'}}>Cancel</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={styles.existingItemContainer}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{...TYPOGRAPHY.BODY_SM, fontWeight: '600'}}>{entity.legal_name}</div>
                        {entity.dba_name && <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY}}>DBA: {entity.dba_name}</div>}
                        {entity.ein && <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY, fontSize: '11px'}}>EIN: {entity.ein}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: SPACING.XS }}>
                        <button onClick={() => startEditEntity(entity)} style={styles.buttonTiny}>Edit</button>
                        <button onClick={() => handleDeleteEntity(entity.id)} style={{...styles.buttonTiny, backgroundColor: COLORS.ERROR}}>Delete</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {entityError && <div style={styles.error}>{entityError}</div>}
            {entitySuccess && <div style={{ ...styles.error, backgroundColor: '#d1fae5', color: '#065f46' }}>{entitySuccess}</div>}

            <div style={{paddingTop: SPACING.SM, borderTop: `1px solid ${COLORS.BORDER_LIGHT}`, marginTop: SPACING.SM}}>
              <form onSubmit={handleEntitySubmit} style={styles.subForm}>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Legal Name *</label>
                <input type="text" name="legal_name" value={entityForm.legal_name} onChange={handleEntityChange} placeholder="Company Legal Name LLC" style={styles.input} required />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>DBA Name</label>
                <input type="text" name="dba_name" value={entityForm.dba_name} onChange={handleEntityChange} placeholder="Doing Business As" style={styles.input} />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>EIN</label>
                <input type="text" name="ein" value={entityForm.ein} onChange={handleEntityChange} placeholder="12-3456789" style={styles.input} />
              </div>
              {/* Business Address - Structured Fields */}
              <div style={{...styles.compactFormGroup, borderTop: `1px solid ${COLORS.BORDER_LIGHT}`, paddingTop: SPACING.SM, marginTop: SPACING.SM}}>
                <label style={{...styles.label, fontSize: '11px', color: COLORS.TEXT_SECONDARY}}>Business Address</label>
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Street Address</label>
                <input type="text" name="business_address_line_1" value={entityForm.business_address_line_1} onChange={handleEntityChange} placeholder="Street address" style={styles.input} />
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>Apt/Suite</label>
                <input type="text" name="business_address_line_2" value={entityForm.business_address_line_2} onChange={handleEntityChange} placeholder="Suite, apt, etc." style={styles.input} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING.SM }}>
                <div style={styles.compactFormGroup}>
                  <label style={styles.label}>City</label>
                  <input type="text" name="business_city" value={entityForm.business_city} onChange={handleEntityChange} placeholder="City" style={styles.input} />
                </div>
                <div style={styles.compactFormGroup}>
                  <label style={styles.label}>State</label>
                  <select name="business_state" value={entityForm.business_state} onChange={handleEntityChange} style={styles.input}>
                    <option value="">— Select —</option>
                    {US_STATES.map(state => <option key={state.code} value={state.code}>{state.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={styles.compactFormGroup}>
                <label style={styles.label}>ZIP</label>
                <input type="text" name="business_zip" value={entityForm.business_zip} onChange={handleEntityChange} placeholder="ZIP code" style={styles.input} />
              </div>

              {/* Contact Address - Auto-populated from Primary Owner */}
              <div style={{...styles.compactFormGroup, borderTop: `1px solid ${COLORS.BORDER_LIGHT}`, paddingTop: SPACING.SM, marginTop: SPACING.SM}}>
                <label style={styles.label}>Contact Address</label>
                <div style={{...TYPOGRAPHY.LABEL, fontSize: '11px', color: COLORS.TEXT_SECONDARY, marginBottom: SPACING.XS}}>Auto-filled from primary owner</div>
                <textarea name="contact_address" value={entityForm.contact_address} onChange={handleEntityChange} placeholder="(auto-populated)" style={{ ...styles.input, minHeight: '60px', fontFamily: 'monospace', backgroundColor: '#f9fafb' }} />
              </div>
              <button type="submit" style={styles.buttonSmall}>+ Add Entity</button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Notes Section - Full Width Bottom */}
      <form onSubmit={handleSubmit} style={{ ...styles.card, marginTop: SPACING.XL }}>
        <h3 style={styles.sectionTitle}>Notes</h3>
        <div style={styles.compactFormGroup}>
          <textarea
            name="notes"
            value={formData.notes || ''}
            onChange={handleChange}
            placeholder="Add any additional notes about this merchant..."
            style={{ ...styles.input, minHeight: '100px', fontFamily: 'monospace' }}
          />
        </div>
      </form>

      <div style={{ marginTop: SPACING.MD, textAlign: 'center' }}>
        <button type="button" onClick={() => navigate(`/merchants/${id}`)} style={styles.buttonSecondary}>Cancel</button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: `${SPACING.MD} ${SPACING.XL} ${SPACING.XXL} ${SPACING.XL}`,
    maxWidth: '1400px',
    margin: '0 auto',
  },
  gridLayout: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
    gap: SPACING.XL,
    marginBottom: SPACING.XXXL,
  },
  card: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    padding: SPACING.MD,
    boxShadow: SHADOWS.DEFAULT,
  },
  sectionTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_PRIMARY,
    margin: `0 0 ${SPACING.MD} 0`,
    paddingBottom: SPACING.SM,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },
  subForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.SM,
  },
  compactFormGroup: {
    marginBottom: 0,
  },
  label: {
    ...TYPOGRAPHY.LABEL,
    color: COLORS.TEXT_SECONDARY,
    display: 'block',
    marginBottom: SPACING.XS,
    fontSize: '12px',
  },
  input: {
    width: '100%',
    padding: `${SPACING.XS} ${SPACING.SM}`,
    fontSize: '14px',
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  buttonPrimary: {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontWeight: '600',
    cursor: 'pointer',
  },
  buttonSmall: {
    padding: `${SPACING.XS} ${SPACING.SM}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: SPACING.SM,
  },
  buttonTiny: {
    padding: `${SPACING.XS} ${SPACING.XS}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  existingItemContainer: {
    padding: SPACING.SM,
    backgroundColor: '#f9fafb',
    borderRadius: RADIUS.DEFAULT,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
  },
  buttonSecondary: {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: '#e5e7eb',
    color: COLORS.TEXT_PRIMARY,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontWeight: '600',
    cursor: 'pointer',
  },
  error: {
    padding: SPACING.SM,
    backgroundColor: '#fee2e2',
    borderRadius: RADIUS.DEFAULT,
    color: COLORS.ERROR,
    marginBottom: SPACING.SM,
    fontSize: '13px',
  },
};
